import BlockedDatePopup, { BlockedDatePopupProps } from 'components/popups/BlockedDatePopup'
import ReactCalendar, { CalendarProps, TileArgs } from 'react-calendar'
import React, { useCallback, useState } from 'react'
import useTranslation from 'hooks/useTranslation'
import { dateToYMDformat } from 'utils/date'

export type BlockedDateReason = 'beforeMin' | 'afterMax'

type DateLike = Date | string | null | undefined
type CalendarValue = CalendarProps['value']

export interface BlockedDateModal {
  title: string
  description?: React.ReactNode
  /** onClick dostaje kliknieta date; modal zamyka sie automatycznie */
  action?: { label: string, onClick: (date: Date) => void }
  backLabel?: string
}

export interface SoftLimitCalendarProps extends Omit<CalendarProps, 'selectRange' | 'allowPartialRange'> {
  /** daty wczesniejsze sa widoczne i klikalne, ale zamiast wyboru pokazuja modal (Date albo YYYY-MM-DD) */
  softMinDate?: DateLike
  /** daty pozniejsze sa widoczne i klikalne, ale zamiast wyboru pokazuja modal (Date albo YYYY-MM-DD) */
  softMaxDate?: DateLike
  /**
   * Nadpisanie tresci modala dla konkretnego przypadku.
   * - undefined -> domyslny tekst ("zbyt wczesna" / "zbyt pozna" + granica)
   * - obiekt (rowniez czesciowy) -> nadpisuje wybrane pola domyslnego modala
   * - null -> data NIE jest blokowana, zostaje normalnie wybrana
   * `limit` to przekroczona granica (YYYY-MM-DD) - softMinDate albo softMaxDate.
   */
  getBlockedModal?: BlockedModalGetter
}

export type BlockedModalGetter = (reason: BlockedDateReason, date: Date, limit: string) => Partial<BlockedDateModal> | null | undefined

/** propsy miekkich granic - do przekazywania przez komponenty opakowujace kalendarz */
export type SoftLimitProps = Pick<SoftLimitCalendarProps, 'softMinDate' | 'softMaxDate' | 'getBlockedModal'>


const toYMD = (date: DateLike) => !date ? undefined : typeof date === 'string' ? date.slice(0, 10) : dateToYMDformat(date)

/** "2026-10-07" albo Date -> Date o lokalnej polnocy (new Date('YYYY-MM-DD') to polnoc UTC) */
export const toLocalDate = (date: DateLike) => {
  const ymd = toYMD(date)
  if (!ymd) return undefined
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** pierwszy dzien miesiaca daty - wygodna twarda granica dla minDate (nawigacja konczy sie na tym miesiacu) */
export const startOfMonth = (date: DateLike) => {
  const local = toLocalDate(date)
  return local && new Date(local.getFullYear(), local.getMonth(), 1)
}

/** ostatni dzien miesiaca daty - wygodna twarda granica dla maxDate */
export const endOfMonth = (date: DateLike) => {
  const local = toLocalDate(date)
  return local && new Date(local.getFullYear(), local.getMonth() + 1, 0)
}

const formatYMD = (ymd = '') => {
  const [y, m, d] = ymd.split('-')
  return `${d}.${m}.${y}`
}

/**
 * react-calendar z "miekkimi" granicami: zamiast wyszarzonych, nieklikalnych dni (minDate/maxDate)
 * dni spoza zakresu wygladaja na niedostepne, ale po kliknieciu pokazuja modal z wyjasnieniem.
 * minDate/maxDate nadal dzialaja jako twarde granice (wylaczone dni + ograniczenie nawigacji).
 * Obslugiwany jest wybor pojedynczej daty (rowniez value jako zakres + returnValue start/end,
 * jak w DateRangePickerPopup); selectRange nie jest obslugiwane.
 */
const SoftLimitCalendar: React.FC<SoftLimitCalendarProps> = ({
  softMinDate,
  softMaxDate,
  getBlockedModal,
  value,
  defaultValue,
  defaultActiveStartDate,
  onChange,
  onClickDay,
  tileClassName,
  ...props
}) => {
  const { t } = useTranslation('basic.blockedDate')
  // kalendarz zawsze kontrolowany - inaczej react-calendar zaznaczylby zablokowana date
  const [innerValue, setInnerValue] = useState<CalendarValue>(defaultValue ?? null)
  const [blocked, setBlocked] = useState<{ date: Date, modal: BlockedDateModal }>()

  const minYMD = toYMD(softMinDate)
  const maxYMD = toYMD(softMaxDate)

  // miesiac startowy (jesli nie podano wprost): wybrana data, a gdy jej brak lub lezy poza
  // miekkimi granicami - najblizsza granica. Np. koniec wynajmu bez daty -> miesiac minimalnego konca.
  const [initialActiveStartDate] = useState(() => {
    if (defaultActiveStartDate || props.activeStartDate) return defaultActiveStartDate
    const current = value !== undefined ? value : defaultValue
    const picked = Array.isArray(current)
      ? (props.returnValue === 'end' ? current[1] ?? current[0] : current[0])
      : current
    const ymd = toYMD(picked) ?? dateToYMDformat()
    return toLocalDate(minYMD && ymd < minYMD ? minYMD : maxYMD && ymd > maxYMD ? maxYMD : ymd)
  })

  const getReason = (date: Date): BlockedDateReason | undefined => {
    const ymd = dateToYMDformat(date)
    if (minYMD && ymd < minYMD) return 'beforeMin'
    if (maxYMD && ymd > maxYMD) return 'afterMax'
  }

  const getModal = (date: Date): BlockedDateModal | null => {
    const reason = getReason(date)
    if (!reason) return null
    const limit = (reason === 'beforeMin' ? minYMD : maxYMD) as string
    const custom = getBlockedModal?.(reason, date, limit)
    if (custom === null) return null
    const defaults: BlockedDateModal = reason === 'beforeMin'
      ? { title: t('tooEarlyTitle'), description: t('tooEarlyDesc').replace('{date}', formatYMD(limit)) }
      : { title: t('tooLateTitle'), description: t('tooLateDesc').replace('{date}', formatYMD(limit)) }
    return { ...defaults, ...custom }
  }

  const getTileClassName = (args: TileArgs) => {
    const own = typeof tileClassName === 'function' ? tileClassName(args) : tileClassName
    const soft = args.view === 'month' && getReason(args.date) ? '--softDisabled' : undefined
    return ([] as (string | null | undefined)[]).concat(own, soft).filter(Boolean).join(' ') || null
  }

  const close = useCallback(() => setBlocked(undefined), [])
  const modal = blocked?.modal
  const popupAction: BlockedDatePopupProps['action'] = modal?.action && blocked
    ? {
      label: modal.action.label,
      onClick: () => {
        close()
        modal.action?.onClick(blocked.date)
      },
    }
    : undefined

  return <>
    <ReactCalendar
      {...props}
      value={value !== undefined ? value : innerValue}
      defaultActiveStartDate={initialActiveStartDate}
      tileClassName={getTileClassName}
      onClickDay={(date, event) => {
        if (!getModal(date)) onClickDay?.(date, event)
      }}
      onChange={(next, event) => {
        if (next instanceof Date) {
          const blockedModal = getModal(next)
          if (blockedModal) {
            setBlocked({ date: next, modal: blockedModal })
            return
          }
        }
        if (value === undefined) setInnerValue(next)
        onChange?.(next, event)
      }}
    />
    {modal && <BlockedDatePopup
      title={modal.title}
      description={modal.description}
      action={popupAction}
      backLabel={modal.backLabel}
      close={close}
    />}
  </>
}

export default SoftLimitCalendar
